import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { articleAPI } from '../services/api';
import './CategoryPage.css';

function CategoryPage({ category }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await articleAPI.getAllArticles({ category, limit: 50 });
      setArticles(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const getCategoryColor = (cat) => {
    const colors = {
      crypto: '#f39c12',
      ai: '#3498db',
      politics: '#e74c3c'
    };
    return colors[cat] || '#666';
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  const SentimentIcon = ({ sentiment, size = '14px' }) => {
    if (!sentiment) return null;

    const icons = {
      positive: '📈',
      negative: '📉',
      neutral: '➡️'
    };

    return (
      <span
        style={{
          fontSize: size,
          padding: '0px 0px',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          display: 'inline-flex',
          alignItems: 'center'
        }}
        title={`Sentiment: ${sentiment}`}
      >
        {icons[sentiment]}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading {category} news...</p>
      </div>
    );
  }

  const sortedArticles = [...articles].sort((a, b) => {
    if (a.is_breaking && !b.is_breaking) return -1;
    if (!a.is_breaking && b.is_breaking) return 1;
    return new Date(b.published_at) - new Date(a.published_at);
  });

  return (
    <div className="category-page">
      <div className="category-container">
        <div className="category-header">
          <h1 className="category-title" style={{ borderColor: getCategoryColor(category) }}>
            {category.toUpperCase()} NEWS
          </h1>
          <p className="category-subtitle">
            {sortedArticles.length} articles
          </p>
        </div>

        {sortedArticles.length === 0 ? (
          <div className="no-articles">
            <h2>No {category} articles found</h2>
            <p>Check back soon for the latest {category} news!</p>
            <Link to="/" className="back-button">← Back to Home</Link>
          </div>
        ) : (
          <div className="category-grid">
            {sortedArticles.map(article => (
              <article key={article.id} className="category-article">
                {article.image_url ? (
                  <Link to={`/article/${article.id}`}>
                    <img
                      src={article.image_url}
                      alt={article.title}
                      className="category-article-image"
                    />
                  </Link>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '200px',
                    backgroundColor: getCategoryColor(article.category),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '28px',
                    fontWeight: 'bold',
                    opacity: 0.3
                  }}>
                    {article.category?.toUpperCase()}
                  </div>
                )}
                <div className="category-article-content">
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <span
                      className="category-badge"
                      style={{ backgroundColor: getCategoryColor(article.category) }}
                    >
                      {article.category?.toUpperCase()}
                    </span>

                    {article.is_breaking && (
                      <span className="breaking-badge-small">🔥 BREAKING</span>
                    )}
                      <SentimentIcon sentiment={article.sentiment} size="18px" />
                  </div>
                  <Link to={`/article/${article.id}`}>
                    <h3 className="category-article-title">{article.title}</h3>
                  </Link>
                  <p className="category-article-summary">
                    {(article.ai_summary || article.summary || '').substring(0, 150)}...
                  </p>
                  <div className="article-meta">
                    <span className="article-source">{article.source_name && (article.source_name.toLowerCase().includes('focusview') ? article.source_name : `${article.source_name} via FocusView.ai`)}</span>
                    <span className="article-time">{formatTimeAgo(article.published_at)}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoryPage;